import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import uniqBy from 'lodash/uniqBy';

import { fetchSvpAssets } from '../assets';
import { getPageComponentsPresetCategories } from '../helpers';
import {
  fetchPageByCategoryId,
  fetchPageBySlug,
  fetchPageByStoryId,
  fetchPageByTagId,
} from '../pages';
import { isCategoryRestricted } from '../../shared/helpers/categoryHelpers';
import {
  fetchSvpAssetById,
  fetchSvpAssetCategoryMatch,
  fetchSvpAssetsByIds,
} from '../assets/actions';
import { fetchSearchResults } from '../search/actions';

import { fetchAllCategories, fetchSvpCategories } from './actions';

import type { Vendor } from '../../types';
import type { CaseReducer } from '@reduxjs/toolkit/src/createReducer';
import type {
  SvpCategory,
  SvpCategoryAdditional,
} from '@vgtv/api-client/lib/svp_category';

export * from './selectors';

export type CategoriesState = Record<
  Vendor,
  {
    items: Record<SvpCategory['id'], SvpCategoryAdditional>;
    categoryMappings: Record<
      // root category id
      SvpCategory['id'],
      Record<SvpCategory['id'], boolean>
    >;
  }
>;

const initialState: CategoriesState = {
  vgtv: {
    items: {},
    categoryMappings: {},
  },
  ab: {
    items: {},
    categoryMappings: {},
  },
  brandstudio: {
    items: {},
    categoryMappings: {},
  },
};

const storeCategoriesFromPageComponentsPreset: CaseReducer<
  CategoriesState,
  Pick<
    ReturnType<
      | typeof fetchPageByCategoryId.fulfilled
      | typeof fetchPageByTagId.fulfilled
      | typeof fetchPageByStoryId.fulfilled
      | typeof fetchPageBySlug.fulfilled
    >,
    'type' | 'payload'
  >
> = (state, action) => {
  if (action.payload) {
    const pageConfig = action.payload;

    const categories = getPageComponentsPresetCategories(pageConfig);

    Object.entries(categories).forEach(([vendor, categories]) => {
      categories.forEach((category) => {
        state[vendor as Vendor].items[category.id] = category;
      });
    });
  }
};

const categoriesReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(fetchSvpCategories.fulfilled, (state, action) => {
      const { vendor } = action.meta.arg;
      const {
        _embedded: { categories },
      } = action.payload;

      categories.forEach((category) => {
        if (!isCategoryRestricted(vendor, category)) {
          state[vendor].items[category.id] = category;
        }
      });
    })
    .addCase(fetchSvpAssets.fulfilled, (state, action) => {
      const { vendor } = action.meta.arg;
      const {
        _embedded: { assets },
      } = action.payload;

      const categories = assets.map((asset) => asset.category);
      const uniqueCategories = uniqBy(categories, (category) => category.id);

      uniqueCategories.forEach((category) => {
        state[vendor].items[category.id] = category;
      });
    })
    .addCase(fetchSvpAssetsByIds.fulfilled, (state, action) => {
      const { vendor } = action.meta.arg;
      const {
        _embedded: { assets },
      } = action.payload;

      const categories = assets.map((asset) => asset.category);
      const uniqueCategories = uniqBy(categories, (category) => category.id);

      uniqueCategories.forEach((category) => {
        state[vendor].items[category.id] = category;
      });
    })
    .addCase(fetchAllCategories.fulfilled, (state, action) => {
      const vendor = action.meta.arg;
      const {
        _embedded: { categories },
      } = action.payload;

      categories.forEach((category) => {
        if (!isCategoryRestricted(vendor, category)) {
          state[vendor].items[category.id] = category;
        }
      });
    })
    .addCase(fetchSvpAssetById.fulfilled, (state, action) => {
      const { vendor } = action.meta.arg;
      const asset = action.payload;

      const category = asset.category;
      state[vendor].items[category.id] = category;
    })
    .addCase(fetchSvpAssetCategoryMatch.fulfilled, (state, action) => {
      const {
        categoryId: rootCategoryId,
        asset: { category: assetCategory, provider },
      } = action.meta.arg;

      if (!(rootCategoryId in state[provider].categoryMappings)) {
        state[provider].categoryMappings[rootCategoryId] = {};
      }

      const assetCategoryId = assetCategory.id;
      const assetParentCategoryId = assetCategory.parentId;

      state[provider].categoryMappings[rootCategoryId][assetCategoryId] =
        action.payload.result;

      if (assetParentCategoryId) {
        state[provider].categoryMappings[rootCategoryId][
          assetParentCategoryId
        ] = action.payload.result;
      }
    })
    .addCase(fetchSearchResults.fulfilled, (state, action) => {
      const { components } = action.payload;

      components.forEach((component) => {
        if (component.data.type === 'categories') {
          const vendor = component.provider ?? action.meta.arg.vendor;

          component.data.preset.forEach((category) => {
            if (!isCategoryRestricted(vendor, category)) {
              state[vendor].items[category.id] = category;
            }
          });
        }
      });
    })
    .addMatcher(
      isAnyOf(
        fetchPageByCategoryId.fulfilled,
        fetchPageByTagId.fulfilled,
        fetchPageByStoryId.fulfilled,
        fetchPageBySlug.fulfilled
      ),
      storeCategoriesFromPageComponentsPreset
    )
);

export {
  categoriesReducer,
  fetchSvpCategories,
  storeCategoriesFromPageComponentsPreset,
};
